import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "components/translations/useTranslation";
import { Button, ButtonType } from "components/common/Button.component";
import Checkbox from "components/common/checkbox.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import { snowplowTrackEvent } from "utils/snowplow";
import { OnboardingProps } from "types/modals";

import image1 from "components/modals/icons/onboarding-01.png";
import image2 from "components/modals/icons/onboarding-02.png";
import image3 from "components/modals/icons/onboarding-03.png";
import { CircleQuestionMark } from "lucide-react";
import "components/taskdetails-remake/panels/draftcomments/datatooltip.scss";

const FIRST_PAGE = 1;
const LAST_PAGE = 3;
const DONT_SHOW_AGAIN_STORAGE_KEY = "onboarding/popup/dontShowAgain";

const PAGE_IMAGES: Record<number, string> = {
    1: image1,
    2: image2,
    3: image3,
};

const trackPageView = (page: number, userData?: Record<string, any>): void => {
    snowplowTrackEvent(
        {
            category: "modular_onboarding",
            action: "view_onboarding_page_" + page,
            label: "view_onboarding_page_" + page,
            value: String(page),
        },
        userData,
    );
};

const Onboarding = ({
    more,
    closeModal,
    loopFocus,
    initialFocusRef,
}: OnboardingProps): React.ReactElement => {
    const { translate } = useTranslation();
    const userData = more?.userData;

    const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
    const [dontShowAgain, setDontShowAgain] = useState<boolean>(true);
    const hasTrackedFirstPage = useRef<boolean>(false);

    useEffect(() => {
        if (!hasTrackedFirstPage.current) {
            hasTrackedFirstPage.current = true;
            trackPageView(FIRST_PAGE, userData);
        }
    }, [userData]);

    const goToNext = useCallback(() => {
        setCurrentPage((prev) => {
            if (prev >= LAST_PAGE) return prev;
            const next = prev + 1;
            trackPageView(next, userData);
            return next;
        });
    }, [userData]);

    const goBack = useCallback(() => {
        setCurrentPage((prev) => (prev > FIRST_PAGE ? prev - 1 : prev));
    }, []);

    const toggleDontShowAgain = useCallback(() => {
        setDontShowAgain((prev) => !prev);
    }, []);

    const handleExit = useCallback(() => {
        window.localStorage.setItem(DONT_SHOW_AGAIN_STORAGE_KEY, String(dontShowAgain));
        snowplowTrackEvent(
            {
                category: "modular_onboarding",
                action: "close_onboarding_modal",
                label: "close_on_page_" + currentPage,
                value: String(currentPage),
            },
            userData,
        );
        closeModal();
    }, [closeModal, currentPage, dontShowAgain, userData]);

    const showBack = currentPage > FIRST_PAGE;
    const showNext = currentPage < LAST_PAGE;
    const showExit = currentPage === LAST_PAGE;

    return (
        <>
            <div className="ga-modal__content">
                <h3>{translate("popUp.onboarding.title_" + currentPage)}</h3>
                <p>{translate("popUp.onboarding.page_" + currentPage)}</p>
                <div className="d-flex justify-content-center">
                    <img
                        alt={"onboarding page " + currentPage}
                        src={PAGE_IMAGES[currentPage]}
                        className="border-0"
                        width={500}
                    />
                </div>
            </div>
            <div className="ga-modal__actions">
                <div className="mr-auto d-flex align-items-center gap-8">
                    <Checkbox
                        inputId="onboarding-dont-show-again"
                        isChecked={dontShowAgain}
                        onChange={toggleDontShowAgain}
                        label={translate("popUp.onboarding.dontShowAgain")}
                    />
                    {showExit && (
                        <span
                            className="d-inline-flex"
                            aria-label={translate("popUp.onboarding.stillSee")}
                            data-tooltip-info={translate("popUp.onboarding.stillSee")}>
                            <CircleQuestionMark/>
                        </span>
                    )}
                </div>
                {showBack && (
                    <Button
                        type={ButtonType.SECONDARY}
                        onClick={goBack}
                        tabIndex={MODAL_CONTENT_TAB_INDEX}
                        text={translate("settings.back")}
                    />
                )}
                {showNext && (
                    <Button
                        type={ButtonType.PRIMARY}
                        onClick={goToNext}
                        ref={initialFocusRef}
                        onBlur={loopFocus}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                        text={translate("popUp.onboarding.showMeMore")}
                    />
                )}
                {showExit && (
                    <Button
                        type={ButtonType.PRIMARY}
                        onClick={handleExit}
                        ref={initialFocusRef}
                        onBlur={loopFocus}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + 1}
                        text={translate("popUp.onboarding.exit")}
                    />
                )}
            </div>
        </>
    );
};

export default Onboarding;
